import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Empty,
  Input,
  Radio,
  Space,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  LockOutlined,
  MergeCellsOutlined,
} from '@ant-design/icons';
import {
  TaxPropertyFolderEntry,
  useGetPropertyFoldersQuery,
  useMergePropertyFoldersMutation,
  useRenamePropertyFolderMutation,
} from '@/store/apis/taxDocumentsApi';
import { displayError, displaySuccess } from '@/utils/display-message-utils';

const { Text } = Typography;

const isLinked = (p: TaxPropertyFolderEntry) => Boolean(p.propertyId);

const yearsOf = (p: TaxPropertyFolderEntry) =>
  Object.keys(p.years ?? {}).sort();

/** Pre-select the property to keep on merge: a linked one wins (source of
 *  truth), otherwise the longest name (usually the fullest address). */
const defaultTarget = (props: TaxPropertyFolderEntry[]): string => {
  const linked = props.find(isLinked);
  if (linked) return linked.key;
  return [...props].sort(
    (a, b) => b.displayName.length - a.displayName.length,
  )[0].key;
};

interface ManagePropertiesDrawerProps {
  open: boolean;
  onClose: () => void;
  dealId?: string;
}

const ManagePropertiesDrawer: React.FC<ManagePropertiesDrawerProps> = ({
  open,
  onClose,
  dealId,
}) => {
  const { token } = theme.useToken();
  const { data, isFetching } = useGetPropertyFoldersQuery(dealId ?? '', {
    skip: !open || !dealId,
  });
  const [renameProperty, { isLoading: renaming }] =
    useRenamePropertyFolderMutation();
  const [mergeProperties, { isLoading: merging }] =
    useMergePropertyFoldersMutation();

  const properties = useMemo(() => data?.properties ?? [], [data]);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [mode, setMode] = useState<'list' | 'merge'>('list');
  const [targetKey, setTargetKey] = useState('');

  // Reset transient state whenever the drawer (re)opens or the list changes.
  useEffect(() => {
    if (!open) return;
    setSelectedKeys([]);
    setEditingKey(null);
    setMode('list');
  }, [open, data]);

  const selected = useMemo(
    () => properties.filter((p) => selectedKeys.includes(p.key)),
    [properties, selectedKeys],
  );
  const linkedSelected = selected.filter(isLinked);
  const twoLinked = linkedSelected.length >= 2;
  // A single linked property is forced as the target (source of truth).
  const forcedTarget = linkedSelected.length === 1 ? linkedSelected[0] : null;
  const effectiveTarget = forcedTarget?.key ?? targetKey;

  const toggleSelect = (key: string, checked: boolean) =>
    setSelectedKeys((prev) =>
      checked ? [...prev, key] : prev.filter((k) => k !== key),
    );

  const startRename = (p: TaxPropertyFolderEntry) => {
    setEditingKey(p.key);
    setEditValue(p.displayName);
  };

  const commitRename = async (p: TaxPropertyFolderEntry) => {
    const displayName = editValue.trim();
    if (!displayName || displayName === p.displayName) {
      setEditingKey(null);
      return;
    }
    try {
      await renameProperty({
        taxDealId: dealId!,
        key: p.key,
        displayName,
      }).unwrap();
      displaySuccess('Property renamed.');
      setEditingKey(null);
    } catch (err) {
      displayError(err);
    }
  };

  const openMerge = () => {
    setTargetKey(defaultTarget(selected));
    setMode('merge');
  };

  const confirmMerge = async () => {
    if (twoLinked || !effectiveTarget) return;
    try {
      const result = await mergeProperties({
        taxDealId: dealId!,
        sourceKeys: selectedKeys,
        targetKey: effectiveTarget,
      }).unwrap();
      const targetName =
        result.properties.find((p) => p.key === effectiveTarget)?.displayName ??
        'the target property';
      displaySuccess(`Merged into "${targetName}".`);
      setSelectedKeys([]);
      setMode('list');
    } catch (err) {
      displayError(err);
    }
  };

  const renderBadges = (p: TaxPropertyFolderEntry) => (
    <>
      {isLinked(p) ? (
        <Tag
          color="green"
          icon={<LockOutlined />}
          style={{ fontSize: 11, margin: 0 }}
        >
          Linked
        </Tag>
      ) : (
        p.autoCreated && (
          <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>
            Auto-created
          </Tag>
        )
      )}
      {p.aliases.length > 0 && (
        <Tooltip title={p.aliases.join('\n')}>
          <Tag style={{ fontSize: 11, margin: 0 }}>
            {p.aliases.length} alias{p.aliases.length !== 1 ? 'es' : ''}
          </Tag>
        </Tooltip>
      )}
    </>
  );

  const renderMeta = (p: TaxPropertyFolderEntry) => {
    const years = yearsOf(p);
    return (
      <Space size={[6, 4]} wrap style={{ marginTop: 4 }}>
        {years.length > 0 ? (
          years.map((y) => (
            <Tag key={y} style={{ fontSize: 11, margin: 0 }}>
              FY {y}
            </Tag>
          ))
        ) : (
          <Text type="secondary" style={{ fontSize: 11 }}>
            No documents filed yet
          </Text>
        )}
      </Space>
    );
  };

  const hasFolders = properties.length > 0;

  /**
   * One list, linked properties first.
   *
   * Every tax-scoped linked property is in `properties`, whether or not a Drive
   * folder exists for it yet — so each row behaves the same: selectable,
   * mergeable, and protected from renaming when linked. Linked leads,
   * auto-created follows, which is the order you'd actually read them in.
   */
  const rows = useMemo(
    () => [
      ...properties.filter(isLinked),
      ...properties.filter((p) => !isLinked(p)),
    ],
    [properties],
  );

  const listView = (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
          gap: 8,
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          {selected.length > 0
            ? `${selected.length} selected`
            : 'Select two or more to merge'}
        </Text>
        <Button
          size="small"
          type="primary"
          icon={<MergeCellsOutlined />}
          disabled={selected.length < 2}
          onClick={openMerge}
        >
          Merge selected{selected.length >= 2 ? ` (${selected.length})` : ''}
        </Button>
      </div>

      {!hasFolders ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No properties or property folders"
        />
      ) : (
        <div
          style={{
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          {rows.map((p, i) => {
            const checked = selectedKeys.includes(p.key);
            const editing = editingKey === p.key;
            const linked = isLinked(p);
            return (
              <div
                key={p.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '10px 12px',
                  borderTop:
                    i === 0
                      ? 'none'
                      : `1px solid ${token.colorBorderSecondary}`,
                  backgroundColor: checked
                    ? token.colorPrimaryBg
                    : 'transparent',
                }}
              >
                <Checkbox
                  checked={checked}
                  disabled={editing}
                  onChange={(e) => toggleSelect(p.key, e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {editing ? (
                    <Space.Compact style={{ width: '100%' }}>
                      <Input
                        size="small"
                        autoFocus
                        value={editValue}
                        disabled={renaming}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void commitRename(p);
                          if (e.key === 'Escape') setEditingKey(null);
                        }}
                      />
                      <Button
                        size="small"
                        type="primary"
                        icon={<CheckOutlined />}
                        loading={renaming}
                        onClick={() => void commitRename(p)}
                      />
                      <Button
                        size="small"
                        icon={<CloseOutlined />}
                        disabled={renaming}
                        onClick={() => setEditingKey(null)}
                      />
                    </Space.Compact>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Text strong style={{ fontSize: 13 }}>
                        {p.displayName}
                      </Text>
                      {linked ? (
                        <Tooltip title="Linked properties are the source of truth and can't be renamed here">
                          <LockOutlined
                            style={{
                              fontSize: 12,
                              color: token.colorTextQuaternary,
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Rename">
                          <Button
                            size="small"
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => startRename(p)}
                            style={{ height: 20, padding: '0 4px' }}
                          />
                        </Tooltip>
                      )}
                      {renderBadges(p)}
                    </div>
                  )}
                  {renderMeta(p)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  const target = selected.find((p) => p.key === effectiveTarget);
  const absorbed = selected.filter((p) => p.key !== effectiveTarget);
  const combinedYears = Array.from(new Set(selected.flatMap(yearsOf))).sort();

  const mergeView = (
    <>
      <Button
        type="text"
        size="small"
        icon={<ArrowLeftOutlined />}
        onClick={() => setMode('list')}
        style={{ marginBottom: 8, paddingLeft: 0 }}
      >
        Back
      </Button>

      <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 14 }}>
        Merge {selected.length} properties
      </Text>

      {twoLinked ? (
        <Alert
          type="error"
          showIcon
          message="Can't merge two linked properties"
          description="Linked properties are the source of truth (backed by a Property record). Unselect all but one linked property to merge into it."
          style={{ marginBottom: 12 }}
        />
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginBottom: 6 }}
            >
              Keep which property? (its Drive folder and name are kept)
            </Text>
            {forcedTarget ? (
              <div
                style={{
                  padding: '8px 10px',
                  border: `1px solid ${token.colorBorderSecondary}`,
                  borderRadius: 6,
                }}
              >
                <Space>
                  <Text strong style={{ fontSize: 13 }}>
                    {forcedTarget.displayName}
                  </Text>
                  <Tag
                    color="green"
                    icon={<LockOutlined />}
                    style={{ fontSize: 11, margin: 0 }}
                  >
                    Linked
                  </Tag>
                </Space>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: 'block', marginTop: 2 }}
                >
                  Linked property is kept as the target automatically.
                </Text>
              </div>
            ) : (
              <Radio.Group
                value={targetKey}
                onChange={(e) => setTargetKey(e.target.value)}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  {selected.map((p) => (
                    <Radio
                      key={p.key}
                      value={p.key}
                      style={{
                        padding: '6px 10px',
                        border: `1px solid ${token.colorBorderSecondary}`,
                        borderRadius: 6,
                        width: '100%',
                        marginInlineEnd: 0,
                      }}
                    >
                      {p.displayName}
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            )}
          </div>

          <Alert
            type="warning"
            showIcon
            message="What happens on merge"
            description={
              <Space direction="vertical" size={4}>
                <Text style={{ fontSize: 12 }}>
                  All files under{' '}
                  {absorbed.map((p, i) => (
                    <React.Fragment key={p.key}>
                      <Text strong style={{ fontSize: 12 }}>
                        {p.displayName}
                      </Text>
                      {i < absorbed.length - 1 ? ', ' : ''}
                    </React.Fragment>
                  ))}{' '}
                  move into{' '}
                  <Text strong style={{ fontSize: 12 }}>
                    {target?.displayName ?? '—'}
                  </Text>
                  , and their now-empty Drive folders are deleted.
                </Text>
                <Text style={{ fontSize: 12 }}>
                  Documents are re-filed under{' '}
                  <Text strong style={{ fontSize: 12 }}>
                    {target?.displayName ?? '—'}
                  </Text>
                  {combinedYears.length > 0 && (
                    <> across FY {combinedYears.join(', ')}</>
                  )}
                  .
                </Text>
                <Text type="danger" style={{ fontSize: 11 }}>
                  This moves and deletes folders in Google Drive and can't be
                  undone.
                </Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
          />
        </>
      )}
    </>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === 'list' ? 'Manage Properties' : 'Merge Properties'}
      width={640}
      maskClosable={!renaming && !merging}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {mode === 'list' ? (
            <Button onClick={onClose} disabled={renaming}>
              Done
            </Button>
          ) : (
            <Space>
              <Button onClick={() => setMode('list')} disabled={merging}>
                Back
              </Button>
              <Button
                type="primary"
                danger
                icon={<MergeCellsOutlined />}
                loading={merging}
                disabled={twoLinked || !effectiveTarget}
                onClick={() => void confirmMerge()}
              >
                Confirm merge
              </Button>
            </Space>
          )}
        </div>
      }
      destroyOnHidden
      loading={isFetching && properties.length === 0}
    >
      {mode === 'list' ? listView : mergeView}
    </Drawer>
  );
};

export default ManagePropertiesDrawer;
